// src/context/persona/assist/assistModel.js — every rule of the persona AI aids that needs no React. PURE.
import { emptyHalf } from '../personaModel.js';

export const PROPOSE_MAX = 2000;
export const NAME_MAX = 120;
export const TEXT_MAX = 3000;          // create: who, pain_gain
export const INSTRUCTION_MAX = 2000;   // personalize
export const AGE_MIN = 15;
export const AGE_MAX = 65;
export const POLL_MS = 4000;
export const MARKETS = ['b2b', 'any', 'b2c'];
export const GENDERS = ['any', 'female', 'male'];

const trim = (v) => (typeof v === 'string' ? v.trim() : '');

export function proposeProblem(request) {
  const r = trim(request);
  if (!r) return 'required';
  if (r.length > PROPOSE_MAX) return 'tooLong';
  return null;
}

/** The create form → {field: reasonKey}. Empty = valid. `ageMax` '' / null = "65+". */
export function createProblems(form) {
  const p = {};
  const name = trim(form.name);
  if (!name) p.name = 'required'; else if (name.length > NAME_MAX) p.name = 'tooLong';
  if (!MARKETS.includes(form.market)) p.market = 'required';
  const min = Number(form.ageMin);
  if (!Number.isInteger(min) || min < AGE_MIN || min > AGE_MAX) p.ageMin = 'age';
  if (form.ageMax !== '' && form.ageMax != null) {
    const max = Number(form.ageMax);
    if (!Number.isInteger(max) || max < AGE_MIN || max > AGE_MAX) p.ageMax = 'age';
    else if (!p.ageMin && max < min) p.ageMax = 'ageOrder';
  }
  if (trim(form.who).length > TEXT_MAX) p.who = 'tooLong';
  if (trim(form.painGain).length > TEXT_MAX) p.painGain = 'tooLong';
  return p;
}

export function buildCreateBody(form, documents = []) {
  const body = {
    kind: 'create',
    name: trim(form.name),
    market: form.market,
    gender: GENDERS.includes(form.gender) ? form.gender : 'any',
    age_min: Number(form.ageMin),
    age_max: form.ageMax === '' || form.ageMax == null ? null : Number(form.ageMax),
  };
  if (trim(form.who)) body.who = trim(form.who);
  if (trim(form.painGain)) body.pain_gain = trim(form.painGain);
  if (documents.length) body.documents = documents;
  return body;
}

export function buildPersonalizeBody(personaId, instruction, documents = []) {
  const body = { kind: 'personalize', persona_id: personaId };
  if (trim(instruction)) body.instruction = trim(instruction);
  if (documents.length) body.documents = documents;
  return body;
}

export const isActive = (job) => job?.status === 'pending' || job?.status === 'running';

/** 'running' | 'stale' | 'completed' | 'failed' | null */
export function jobView(job) {
  if (!job) return null;
  if (isActive(job)) return job.stale ? 'stale' : 'running';
  if (job.status === 'completed') return job.proposal ? 'completed' : 'failed';
  if (job.status === 'failed') return 'failed';
  return null;
}

/** "invented_number who.context: 30" → { 'who.context': ['30'] } (ITALIAN half paths). */
export function parseWarnings(warnings) {
  const out = {};
  (warnings || []).forEach((w) => {
    const m = /^invented_number\s+(.+?):\s*(.+)$/.exec(String(w));
    if (!m) return;
    (out[m[1]] = out[m[1]] || []).push(m[2]);
  });
  return out;
}

/** A completed job's proposal → the editor's draft (an absent English half becomes an empty one). */
export function proposalToDraft(proposal) {
  const clone = (x) => JSON.parse(JSON.stringify(x));
  const it = clone(proposal.it);
  return {
    market: proposal.market,
    it,
    en: proposal.en ? clone(proposal.en) : emptyHalf(it.pain_gain.format),
  };
}

/**
 * Is the newest completed job's proposal still UNSAVED (decision 6)? `rows` = the persona list;
 * `currentEtag` = the IT etag read for a personalize job's persona (undefined when not read).
 */
export function isUnsavedProposal(job, rows, currentEtag) {
  if (jobView(job) !== 'completed') return false;
  if (job.kind === 'create') return !(rows || []).some((r) => r.id === job.suggested_id);
  if (job.kind === 'personalize') return Boolean(job.base_etag) && currentEtag === job.base_etag;
  return false;
}
